import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MentorshipService, {
    MATCHING_GATE_LOCKED_COPY,
    MentorshipCampusConfig,
    MentorshipCohortDetail,
    MentorshipCohortProgress,
    MentorshipDomainTaxonomy,
    MentorshipMatchingGate,
    MentorshipMatchingStats,
    MentorshipMenteePoolListResult,
} from './MentorshipService';
import MentorshipLogInteractionModal from './MentorshipLogInteractionModal';
import MentorshipScheduleSessionModal from './MentorshipScheduleSessionModal';
import './MentorshipOverviewVisual.css';

/**
 * P7-BATCH-005/006: UAS-2372 setup wiring + UAS-2373 Live/Completed cards.
 * P7-VISUAL-R1: visual restyle only; values, GETs, and honesty rules unchanged.
 * P9-BATCH-007: Overview Log Interaction opens the admin logger for Published pairs.
 * P9-BATCH-008: KPI cards + milestone bars from GET .../progress (derived overlay).
 */
export type OverviewNavigateSection = 'campus' | 'pool' | 'mentee-pool' | 'matching';

interface MentorshipOverviewProps {
    cohortId: number | null;
    disabled?: boolean;
    cohortName?: string | null;
    instituteId?: number | null;
    onConfigureModule: () => void;
    onNavigateSection: (section: OverviewNavigateSection) => void;
}

const PLACEHOLDER = '\u2014';
// R-QA-01 intake 31 ISSUE-4 (REQ-2372-21): View all opens the existing Activity Log Report
// pre-filtered to Career Guidance & Mentorship. The report reads and validates `?category=`
// against ACTIVITY_CATEGORIES (ActivityLogReport.tsx), so this is its existing contract —
// no new route and no invented writers.
const ACTIVITY_LOG_PATH = '/management/track/activity-log-report';
const ACTIVITY_LOG_MENTORSHIP_CATEGORY = 'MENTORING_GUIDANCE';
const ACTIVITY_LOG_MENTORSHIP_HREF = `${ACTIVITY_LOG_PATH}?category=${ACTIVITY_LOG_MENTORSHIP_CATEGORY}`;

/** REQ-2372-07 — last stage is Cohort Ended; ignore prototype Renewal / Reshuffle. */
const STEPPER_STAGES = [
    'Configure',
    'Mentor Enrolment',
    'Mentee Registration',
    'Matching',
    'Cohort Live',
    'Cohort Ended',
] as const;

type OverviewStatus = 'setup' | 'live' | 'completed';
type OverviewLayout = 'setup' | 'live';
type StageVisual = 'complete' | 'current' | 'upcoming' | 'unknown';

type LoadState<T> =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'ready'; data: T };

const loadingState = <T,>(): LoadState<T> => ({ status: 'loading' });

function fromSettled<T>(result: PromiseSettledResult<T>): LoadState<T> {
    if (result.status === 'fulfilled') {
        return { status: 'ready', data: result.value };
    }
    return { status: 'error', message: MentorshipService.getErrorMessage(result.reason) };
}

function isReady<T>(state: LoadState<T> | null): state is { status: 'ready'; data: T } {
    return state != null && state.status === 'ready';
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/** Institute-local calendar date YYYY-MM-DD (DEC-P7-010 — not datetime). */
function todayCalendarDate(): string {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

function calendarDate(value: string | null | undefined): string | null {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    return text.slice(0, 10);
}

/**
 * DEC-P7-010 as amended by R-ADMIN-01: Completed / Live require Configure complete
 * (DEC-P7-002). Unconfigured cohorts stay Setup in Progress regardless of dates.
 * Otherwise: Completed if end_date set and end_date < today;
 * Live if start_date set and start_date <= today and (end_date null or end_date >= today);
 * else Setup in Progress. Null dates → Setup.
 */
function deriveOverviewStatus(
    startDate: string | null,
    endDate: string | null,
    configureComplete: boolean
): OverviewStatus {
    if (!configureComplete) return 'setup';
    const today = todayCalendarDate();
    const start = calendarDate(startDate);
    const end = calendarDate(endDate);
    if (end != null && end < today) return 'completed';
    if (start != null && start <= today && (end == null || end >= today)) return 'live';
    return 'setup';
}

function statusLabel(status: OverviewStatus): string {
    if (status === 'completed') return 'Cohort Completed';
    if (status === 'live') return 'Cohort Live';
    return 'Setup in Progress';
}

/**
 * DEC-P7-002: Configure complete when every associated campus has a saved campus-config
 * row and domain taxonomy has been saved at least once. Identity-create alone is not enough.
 * Honest signal for "taxonomy saved once" = at least one persisted track id (GET has no saved_at).
 */
function isConfigureComplete(cohort: MentorshipCohortDetail, taxonomy: MentorshipDomainTaxonomy): boolean {
    const associatedIds = cohort.associatedCampusIds || [];
    if (associatedIds.length === 0) return false;
    const allCampusesSaved = associatedIds.every((campusId) =>
        (cohort.campuses || []).some((row) => row.campusId === campusId && row.id != null)
    );
    const taxonomySavedOnce = (taxonomy.tracks || []).some((track) => track.id != null);
    return allCampusesSaved && taxonomySavedOnce;
}

/**
 * DEC-P7-006: Mentor Enrolment complete when confirmed_or_onboarded_mentors >= required_sum
 * (gate numbers, not exact-division neededMentors). Missing required_sum → not complete.
 */
function isMentorEnrolmentComplete(gate: MentorshipMatchingGate): boolean {
    if (gate.requiredSum == null) return false;
    return gate.confirmedOrOnboardedMentors >= gate.requiredSum;
}

/** DEC-P7-003 interim: Mentee Registration started = at least one Registered pool row. */
function isMenteeRegistrationStarted(registeredCount: number): boolean {
    return registeredCount >= 1;
}

/**
 * REQ-2372-11 complete when mentee target is met. Target = sum of campus mentee_target
 * (DEC-P7-007). Any missing menteeTarget → cannot claim complete.
 */
function menteeTargetSum(campuses: MentorshipCampusConfig[]): number | null {
    if (!campuses.length) return null;
    let sum = 0;
    for (const campus of campuses) {
        if (campus.menteeTarget == null) return null;
        sum += campus.menteeTarget;
    }
    // Campus-wise config outranks the All Campuses default: overridden campuses each contribute
    // their own target, and the campuses still inheriting share the default, counted once.
    // Before the has_campus_override column exists, fall back to the uniform-value heuristic.
    const overridden = campuses.filter((campus) => campus.hasCampusOverride);
    const inherited = campuses.filter((campus) => !campus.hasCampusOverride);
    if (overridden.length > 0) {
        return (
            overridden.reduce((total, campus) => total + (campus.menteeTarget as number), 0) +
            (inherited.length > 0 ? (inherited[0].menteeTarget as number) : 0)
        );
    }
    if (isUniformMenteeTarget(campuses)) {
        return campuses[0].menteeTarget as number;
    }
    return sum;
}

/** Every campus carries the same target — the shape produced by the All Campuses tab. */
function isUniformMenteeTarget(campuses: MentorshipCampusConfig[]): boolean {
    if (campuses.length < 2) return false;
    const first = campuses[0].menteeTarget;
    if (first == null) return false;
    return campuses.every((campus) => campus.menteeTarget === first);
}

function isMenteeRegistrationComplete(registeredCount: number, target: number | null): boolean {
    if (target == null) return false;
    return registeredCount >= target;
}

/**
 * DEC-P7-014: Matching complete when matching_lock_in_effect (EXISTS COMPLETED run),
 * not when pairs are Published.
 */
function isMatchingComplete(lockInEffect: boolean): boolean {
    return lockInEffect === true;
}

function gatePercent(gate: MentorshipMatchingGate): number | null {
    if (gate.threshold == null || gate.threshold <= 0) return null;
    return Math.round((gate.confirmedOrOnboardedMentors / gate.threshold) * 100);
}

function mentorsNeededForThreshold(gate: MentorshipMatchingGate): number | null {
    if (gate.threshold == null) return null;
    return Math.max(0, gate.threshold - gate.confirmedOrOnboardedMentors);
}

function formatDateRange(startDate: string | null, endDate: string | null): string {
    const start = calendarDate(startDate);
    const end = calendarDate(endDate);
    if (start == null && end == null) return PLACEHOLDER;
    return `${start ?? PLACEHOLDER} \u2013 ${end ?? PLACEHOLDER}`;
}

function stageStepClass(visual: StageVisual): string {
    if (visual === 'complete') return 'ms-ov-step is-complete';
    if (visual === 'current') return 'ms-ov-step is-current';
    return 'ms-ov-step';
}

const MentorshipOverview = ({
    cohortId,
    disabled = false,
    cohortName = null,
    instituteId = null,
    onConfigureModule,
    onNavigateSection,
}: MentorshipOverviewProps) => {
    const cohortAvailable = cohortId != null && !disabled;

    const [cohortState, setCohortState] = useState<LoadState<MentorshipCohortDetail> | null>(null);
    const [gateState, setGateState] = useState<LoadState<MentorshipMatchingGate> | null>(null);
    const [statsState, setStatsState] = useState<LoadState<MentorshipMatchingStats> | null>(null);
    const [taxonomyState, setTaxonomyState] = useState<LoadState<MentorshipDomainTaxonomy> | null>(null);
    const [menteePoolState, setMenteePoolState] = useState<LoadState<MentorshipMenteePoolListResult> | null>(null);
    const [progressState, setProgressState] = useState<LoadState<MentorshipCohortProgress> | null>(null);
    const [showLogModal, setShowLogModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [exportBusy, setExportBusy] = useState(false);
    const [exportError, setExportError] = useState('');

    useEffect(() => {
        if (!cohortAvailable || cohortId == null) {
            setCohortState(null);
            setGateState(null);
            setStatsState(null);
            setTaxonomyState(null);
            setMenteePoolState(null);
            setProgressState(null);
            return;
        }

        let cancelled = false;
        setCohortState(loadingState());
        setGateState(loadingState());
        setStatsState(loadingState());
        setTaxonomyState(loadingState());
        setMenteePoolState(loadingState());
        setProgressState(loadingState());

        const load = async () => {
            const [cohortResult, gateResult, statsResult, taxonomyResult, menteeResult, progressResult] =
                await Promise.allSettled([
                    MentorshipService.getCohort(cohortId, instituteId),
                    MentorshipService.getMatchingGate(cohortId, instituteId),
                    MentorshipService.getMatchingStats(cohortId, instituteId),
                    MentorshipService.getDomainTaxonomy(cohortId, instituteId),
                    MentorshipService.listMenteePool(
                        cohortId,
                        { confirmationStatus: ['Registered'], page: 1, pageSize: 1 },
                        instituteId
                    ),
                    MentorshipService.getCohortProgress(cohortId, instituteId),
                ]);
            if (cancelled) return;
            setCohortState(fromSettled(cohortResult));
            setGateState(fromSettled(gateResult));
            setStatsState(fromSettled(statsResult));
            setTaxonomyState(fromSettled(taxonomyResult));
            setMenteePoolState(fromSettled(menteeResult));
            setProgressState(fromSettled(progressResult));
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [cohortAvailable, cohortId, instituteId]);

    const cohort = isReady(cohortState) ? cohortState.data : null;
    const gate = isReady(gateState) ? gateState.data : null;
    const stats = isReady(statsState) ? statsState.data : null;
    const taxonomy = isReady(taxonomyState) ? taxonomyState.data : null;
    const progress = isReady(progressState) ? progressState.data : null;

    const registeredMentees = useMemo(() => {
        if (isReady(menteePoolState)) return menteePoolState.data.pagination.total;
        if (gate) return gate.registeredMentees;
        return null;
    }, [menteePoolState, gate]);

    const configureComplete = cohort != null && taxonomy != null ? isConfigureComplete(cohort, taxonomy) : null;
    const overviewStatus = cohort
        ? deriveOverviewStatus(cohort.startDate, cohort.endDate, configureComplete === true)
        : null;
    const layout: OverviewLayout | null = overviewStatus == null ? null : overviewStatus === 'setup' ? 'setup' : 'live';
    const targetSum = cohort ? menteeTargetSum(cohort.campuses || []) : null;
    const matchingStarted = stats != null && stats.currentRunId != null;
    const mentorComplete = gate != null ? isMentorEnrolmentComplete(gate) : null;
    const menteeStarted = registeredMentees != null ? isMenteeRegistrationStarted(registeredMentees) : null;
    const menteeComplete =
        registeredMentees != null ? isMenteeRegistrationComplete(registeredMentees, targetSum) : null;
    const matchingComplete = cohort != null ? isMatchingComplete(cohort.matchingLockInEffect) : null;
    const matchingUnlocked = gate != null ? gate.unlocked === true : null;

    const stageVisuals: StageVisual[] = useMemo(() => {
        const visuals: StageVisual[] = ['upcoming', 'upcoming', 'upcoming', 'upcoming', 'upcoming', 'upcoming'];

        // Configure — DEC-P7-002
        if (configureComplete == null) visuals[0] = 'unknown';
        else if (configureComplete) visuals[0] = 'complete';
        else visuals[0] = 'current';

        // Mentor Enrolment — DEC-P7-006; default current after Configure (REQ-2372-09)
        if (mentorComplete == null) {
            visuals[1] = configureComplete === false ? 'upcoming' : 'unknown';
        } else if (mentorComplete) {
            visuals[1] = 'complete';
        } else if (configureComplete === true || (gate != null && gate.confirmedOrOnboardedMentors > 0)) {
            visuals[1] = 'current';
        } else {
            visuals[1] = 'upcoming';
        }

        // Mentee Registration — DEC-P7-003 started; target met = complete
        if (menteeStarted == null) visuals[2] = 'unknown';
        else if (menteeComplete) visuals[2] = 'complete';
        else if (menteeStarted) visuals[2] = 'current';
        else visuals[2] = 'upcoming';

        // Matching — DEC-P7-014 complete = lock; current when gate unlocked (REQ-2372-12)
        if (matchingComplete === true) visuals[3] = 'complete';
        else if (matchingUnlocked === true) visuals[3] = 'current';
        else if (matchingComplete == null && matchingUnlocked == null) visuals[3] = 'unknown';
        else visuals[3] = 'upcoming';

        // Cohort Live / Ended — DEC-P7-010 dates as amended (R-ADMIN-01: dates do not outrank unconfigured); DEC-P7-013 completed → last stage current
        if (overviewStatus == null) {
            visuals[4] = 'unknown';
            visuals[5] = 'unknown';
        } else if (overviewStatus === 'live') {
            visuals[4] = 'current';
            visuals[5] = 'upcoming';
        } else if (overviewStatus === 'completed') {
            visuals[4] = 'complete';
            visuals[5] = 'current';
        } else {
            visuals[4] = 'upcoming';
            visuals[5] = 'upcoming';
        }

        return visuals;
    }, [
        configureComplete,
        mentorComplete,
        gate,
        menteeStarted,
        menteeComplete,
        matchingComplete,
        matchingUnlocked,
        overviewStatus,
    ]);

    if (!cohortAvailable) {
        return <div className="text-muted">Select a cohort to view Overview.</div>;
    }

    const headerName = cohort?.name || cohortName || PLACEHOLDER;
    const campusCount = cohort ? (cohort.associatedCampusIds || []).length : null;
    // Only say "across N campuses" when the figure is genuinely a sum of differing campus values.
    // A uniform target set from All Campuses is one cohort-wide number, so the suffix would misread.
    const targetIsSummed = !isUniformMenteeTarget(cohort?.campuses || []);
    const acrossCampuses =
        targetIsSummed && campusCount != null && campusCount > 1 ? ` across ${campusCount} campuses` : '';
    const dateRange = cohort ? formatDateRange(cohort.startDate, cohort.endDate) : PLACEHOLDER;
    const headerStatus = overviewStatus ? statusLabel(overviewStatus) : null;

    const mentorValue =
        gate == null
            ? null
            : gate.requiredSum == null
              ? String(gate.confirmedOrOnboardedMentors)
              : `${gate.confirmedOrOnboardedMentors}/${gate.requiredSum}`;

    const mentorNote = (() => {
        if (gateState?.status === 'error') return gateState.message;
        if (gateState?.status === 'loading') return 'Loading gate...';
        if (!gate) return undefined;
        const lines: string[] = [];
        if (gate.requiredSum == null) {
            lines.push('Required sum unavailable until every campus has mentee target and max mentees per mentor.');
        }
        const pct = gatePercent(gate);
        if (pct != null) {
            lines.push(`${pct}% of 70% threshold (${gate.confirmedOrOnboardedMentors}/${gate.threshold})`);
        } else {
            lines.push('Gate % unavailable until threshold is configured.');
        }
        lines.push(gate.unlocked ? 'Gate unlocked (PASS).' : `Gate locked: ${MATCHING_GATE_LOCKED_COPY}.`);
        if (cohort) {
            lines.push(
                cohort.matchingLockInEffect ? 'Matching lock in effect.' : 'Matching lock not in effect.'
            );
        }
        if (!gate.unlocked) {
            const needed = mentorsNeededForThreshold(gate);
            if (needed != null) {
                lines.push(`Need ${needed} more confirmed/onboarded mentors to reach threshold.`);
            }
        }
        return lines.join(' ');
    })();

    const menteeValue = registeredMentees;
    const menteeNote = (() => {
        if (menteeValue == null) {
            if (menteePoolState?.status === 'loading' && gateState?.status === 'loading') return 'Loading mentees...';
            if (menteePoolState?.status === 'error' && gateState?.status === 'error') {
                return menteePoolState.message;
            }
            return undefined;
        }
        if (targetSum == null) {
            return 'Target unavailable until every campus has a mentee target.';
        }
        return `${menteeValue} / ${targetSum} target${acrossCampuses}`;
    })();

    const pairsValue = stats == null ? null : stats.distinctMenteesPaired;
    const pairsNote = (() => {
        if (statsState?.status === 'error') return statsState.message;
        if (statsState?.status === 'loading') return 'Loading stats...';
        if (!stats) return undefined;
        if (!matchingStarted) return 'Matching not started';
        return undefined;
    })();

    const confirmedPairingsValue = stats == null ? null : stats.confirmedPairings;
    const confirmedPairingsNote = (() => {
        if (statsState?.status === 'error') return statsState.message;
        if (statsState?.status === 'loading') return 'Loading stats...';
        if (!stats) return undefined;
        if (!matchingStarted) return 'Matching not started';
        return undefined;
    })();

    // Design copy is "{N} pending review". The prototype's 38 is filler — keep the live count and
    // omit the number entirely when stats have not loaded, rather than showing a placeholder one.
    const reviewPairingsNote =
        stats != null ? `${stats.pendingDecision} pending review` : 'Review pending pairings';

    /** Live Published Pairs = stats.publishedPairings (DEC-P7-005). 0 is 0; do not substitute confirmedPairings. */
    const publishedPairsValue = stats == null ? null : stats.publishedPairings;
    const publishedPairsNote = (() => {
        if (statsState?.status === 'error') return statsState.message;
        if (statsState?.status === 'loading') return 'Loading stats...';
        return undefined;
    })();

    const progressLoading = progressState?.status === 'loading';
    const handleExportProgress = async () => {
        if (cohortId == null || exportBusy) return;
        setExportBusy(true);
        setExportError('');
        try {
            await MentorshipService.exportCohortProgress(cohortId, instituteId);
        } catch (error) {
            setExportError(MentorshipService.getErrorMessage(error));
        } finally {
            setExportBusy(false);
        }
    };

    const progressError = progressState?.status === 'error';
    const progressNote = (() => {
        if (progressError) return progressState?.status === 'error' ? progressState.message : undefined;
        if (progressLoading) return 'Loading progress...';
        return undefined;
    })();
    const completeNote = (() => {
        if (progressNote) return progressNote;
        if (progress && !progress.hasDefinition) {
            return 'No definition of done (both milestones off).';
        }
        return undefined;
    })();
    const inactiveNote = (() => {
        if (progressNote) return progressNote;
        if (!progress) return undefined;
        return 'Published pairs with zero events inside the cohort date window.';
    })();
    const sessionBarPercent =
        progress != null && progress.oneToOneSessionsEnabled ? progress.sessionProgressPercent ?? 0 : null;
    const interactionBarPercent =
        progress != null && progress.interactionsEnabled ? progress.interactionProgressPercent ?? 0 : null;

    const menteeCardLoading =
        menteeValue == null && (menteePoolState?.status === 'loading' || gateState?.status === 'loading');
    const menteeCardError =
        menteeValue == null && menteePoolState?.status === 'error' && gateState?.status === 'error';

    const mentorPct = gate != null ? gatePercent(gate) : null;
    const mentorSub =
        gate == null
            ? undefined
            : gate.requiredSum == null
              ? 'Required sum unavailable until every campus has mentee target and max mentees per mentor.'
              : `Target: ${gate.requiredSum}${acrossCampuses}`;
    const mentorChip = mentorPct != null ? `${mentorPct}% · Need 70% to match` : undefined;
    const menteeChip =
        menteeValue != null && targetSum != null
            ? `${menteeValue} / ${targetSum} target${acrossCampuses}`
            : undefined;
    const pairsChip = !matchingStarted ? 'Pending' : undefined;
    // R-QA-01 intake 31 ISSUE-1 (REQ-2372-19): the setup row is exactly four cards —
    // Confirmed Mentors / Registered Mentees / Pairs Matched / Confirmed Pairings.
    // The prototype "Matching Algorithm" 5th card and its lock derivations were removed;
    // the gate lock copy still rides on Confirmed Mentors via mentorChip / mentorNote.

    return (
        <div className="ms-ov">
            <div className="ms-ov-header">
                <div>
                    <h3 className="ms-ov-title">Mentorship Program · Overview</h3>
                    <p className="ms-ov-sub">
                        {headerName}
                        {' · '}
                        {cohortState?.status === 'loading'
                            ? 'Loading campuses...'
                            : campusCount == null
                              ? PLACEHOLDER
                              : `${campusCount} campuses`}
                        {' · '}
                        {cohortState?.status === 'loading' ? 'Loading dates...' : dateRange}
                    </p>
                </div>
                <div className="ms-ov-actions">
                    <button type="button" className="ms-ov-btn ms-ov-btn--ghost" onClick={onConfigureModule}>
                        <i className="bi bi-gear" aria-hidden="true" />
                        Configure Module
                    </button>
                    <button
                        type="button"
                        className="ms-ov-btn ms-ov-btn--green"
                        onClick={() => onNavigateSection('pool')}
                        title="Opens Mentor Pool (existing Add Mentor flow).">
                        <i className="bi bi-plus-lg" aria-hidden="true" />
                        Add a Mentor
                    </button>
                    {layout === 'live' ? (
                        <>
                            <button
                                type="button"
                                className="ms-ov-btn ms-ov-btn--ghost"
                                disabled={!cohortAvailable}
                                onClick={() => setShowScheduleModal(true)}
                                title="Schedule a 1:1 from a mentor's open availability slots.">
                                Schedule Session
                            </button>
                            <button
                                type="button"
                                className="ms-ov-btn ms-ov-btn--ghost"
                                disabled={!cohortAvailable}
                                onClick={() => setShowLogModal(true)}
                                title="Log a session or interaction for a Published pair.">
                                Log Interaction
                            </button>
                        </>
                    ) : null}
                </div>
            </div>

            <div className="ms-ov-status">
                {cohortState?.status === 'error' ? (
                    <span className="text-danger small">{cohortState.message}</span>
                ) : (
                    <>
                        <span className={`ms-ov-chip${overviewStatus === 'setup' ? ' is-on is-setup' : ''}`}>
                            Setup in Progress
                        </span>
                        <span className={`ms-ov-chip${overviewStatus === 'live' ? ' is-on is-live' : ''}`}>
                            <i className="bi bi-circle-fill ms-ov-dot" aria-hidden="true" />
                            Cohort Live
                        </span>
                        {overviewStatus === 'completed' ? (
                            <span className="ms-ov-chip is-on is-done">Cohort Completed</span>
                        ) : headerStatus && overviewStatus !== 'setup' && overviewStatus !== 'live' ? (
                            <span className="ms-ov-chip is-on is-setup">{headerStatus}</span>
                        ) : null}
                    </>
                )}
            </div>

            <div className="d-flex align-items-center flex-wrap gap-2 mb-4 py-2 bg-white px-3 rounded-3 shadow-sm" aria-label="Cohort progress">
                {STEPPER_STAGES.map((stage, index) => (
                    <div key={stage} className="d-flex align-items-center gap-2">
                        <div className={stageStepClass(stageVisuals[index])}>
                            <span className="ms-ov-step-dot">
                                {stageVisuals[index] === 'complete' ? (
                                    <i className="bi bi-check-lg" aria-hidden="true" />
                                ) : (
                                    index + 1
                                )}
                            </span>
                            <span className="ms-ov-step-label">{stage}</span>
                        </div>
                        {index < STEPPER_STAGES.length - 1 ? (
                            <span
                                className={`ms-ov-step-line${
                                    stageVisuals[index] === 'complete' ? ' is-done' : ''
                                }`}
                            />
                        ) : null}
                    </div>
                ))}
            </div>

            {layout !== 'live' && (
                <div className="ms-ov-stats">
                    <OverviewStatCard
                        title="Confirmed Mentors"
                        value={mentorValue}
                        loading={gateState?.status === 'loading'}
                        error={gateState?.status === 'error'}
                        sublabel={mentorSub}
                        chip={mentorChip}
                        chipTone="warn"
                        accent="green"
                        note={mentorNote}
                    />
                    <OverviewStatCard
                        title="Registered Mentees"
                        value={menteeValue}
                        loading={menteeCardLoading}
                        error={menteeCardError}
                        chip={menteeChip}
                        chipTone="ok"
                        accent="blue"
                        note={menteeNote}
                    />
                    <OverviewStatCard
                        title="Pairs Matched"
                        value={pairsValue}
                        loading={statsState?.status === 'loading'}
                        error={statsState?.status === 'error'}
                        chip={pairsChip}
                        chipTone="muted"
                        accent="muted"
                        note={pairsNote}
                    />
                    <OverviewStatCard
                        title="Confirmed Pairings"
                        value={confirmedPairingsValue}
                        loading={statsState?.status === 'loading'}
                        error={statsState?.status === 'error'}
                        accent="muted"
                        note={confirmedPairingsNote}
                    />
                </div>
            )}

            {layout === 'live' && (
                <>
                    <div className="ms-ov-stats">
                        <OverviewStatCard
                            title="Published Pairs"
                            value={publishedPairsValue}
                            loading={statsState?.status === 'loading'}
                            error={statsState?.status === 'error'}
                            accent="green"
                            note={publishedPairsNote}
                        />
                        <OverviewStatCard
                            title="1-1 Sessions Held"
                            value={progress == null ? null : progress.inWindowSessionTotal}
                            loading={progressLoading}
                            error={progressError}
                            accent="blue"
                            note={progressNote}
                        />
                        <OverviewStatCard
                            title="Total Interactions"
                            value={progress == null ? null : progress.inWindowInteractionTotal}
                            loading={progressLoading}
                            error={progressError}
                            accent="blue"
                            note={progressNote}
                        />
                        <OverviewStatCard
                            title="Milestones Completed"
                            value={progress == null ? null : progress.completePairCount}
                            loading={progressLoading}
                            error={progressError}
                            accent="green"
                            note={completeNote}
                        />
                        <OverviewStatCard
                            title="Inactive Pairs"
                            value={progress == null ? null : progress.inactivePairCount}
                            loading={progressLoading}
                            error={progressError}
                            accent="amber"
                            note={inactiveNote}
                        />
                    </div>

                    <div className="ms-ov-split">
                        <div className="ms-ov-section mb-0">
                            <div className="ms-ov-section-head">
                                <h4 className="ms-ov-section-title">Milestone progress</h4>
                            </div>
                            <div className="ms-ov-section-body">
                                {progressLoading ? (
                                    <p className="ms-ov-muted mb-2">Loading progress...</p>
                                ) : progressError ? (
                                    <p className="small text-danger mb-2">
                                        {progressState?.status === 'error'
                                            ? progressState.message
                                            : 'Unable to load progress.'}
                                    </p>
                                ) : progress == null || !progress.hasDefinition ? (
                                    <p className="ms-ov-muted mb-2">
                                        No definition of done. Webinar bar omitted.
                                    </p>
                                ) : (
                                    <p className="ms-ov-muted mb-2">
                                        Average of per-pair capped progress. Webinar bar omitted.
                                    </p>
                                )}
                                <div className="ms-ov-bars">
                                    {sessionBarPercent != null ? (
                                        <div>
                                            <div className="ms-ov-bar-label">
                                                <span>1-1 Sessions</span>
                                                <span>{sessionBarPercent}%</span>
                                            </div>
                                            <div className="ms-ov-bar">
                                                <span style={{ width: `${sessionBarPercent}%` }} />
                                            </div>
                                        </div>
                                    ) : null}
                                    {interactionBarPercent != null ? (
                                        <div>
                                            <div className="ms-ov-bar-label">
                                                <span>Interactions</span>
                                                <span>{interactionBarPercent}%</span>
                                            </div>
                                            <div className="ms-ov-bar">
                                                <span style={{ width: `${interactionBarPercent}%` }} />
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <div className="ms-ov-muted-card">
                            <h4 className="ms-ov-section-title mb-2">Most active pairs</h4>
                            {/* Honest placeholder — session data is not written yet, so no names
                                or ranks are shown. Internal ticket reference removed. */}
                            <p className="mb-0">Available with sessions.</p>
                        </div>
                    </div>

                    <div className="ms-ov-warn">
                        <div>
                            <div className="ms-ov-warn-title">Inactive pairs</div>
                            <div className="small text-muted">
                                {progressLoading
                                    ? 'Loading progress...'
                                    : progressError
                                      ? progressState?.status === 'error'
                                          ? progressState.message
                                          : 'Unable to load progress.'
                                      : progress == null
                                        ? PLACEHOLDER
                                        : `${progress.inactivePairCount} published pair${
                                              progress.inactivePairCount === 1 ? '' : 's'
                                          } with zero in-window events.`}
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="ms-ov-btn ms-ov-btn--ghost"
                                disabled
                                title="Visible only. Does not send.">
                                Send Nudge
                            </button>
                            <button
                                type="button"
                                className="ms-ov-btn ms-ov-btn--ghost"
                                onClick={() => onNavigateSection('matching')}
                                title="Opens Matching.">
                                View Inactive Pairs
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div className="ms-ov-section">
                <div className="ms-ov-section-head">
                    <h4 className="ms-ov-section-title">Quick actions</h4>
                </div>
                <div className="ms-ov-section-body">
                    {layout !== 'live' ? (
                        <div className="ms-ov-qa">
                            <OverviewAction
                                label="Import from Watchlist"
                                note="Pull confirmed alumni"
                                icon="bi-download"
                                onClick={() => onNavigateSection('pool')}
                            />
                            <OverviewAction
                                label="Add Mentor Manually"
                                note="Institute-sourced alumni"
                                icon="bi-person-plus"
                                onClick={() => onNavigateSection('pool')}
                            />
                            <OverviewAction
                                label="Send Student Registration Link"
                                note="Open opt-in for mentees"
                                icon="bi-send"
                                onClick={() => onNavigateSection('mentee-pool')}
                            />
                            <OverviewAction
                                label="Review Pairings"
                                note={reviewPairingsNote}
                                icon="bi-link-45deg"
                                onClick={() => onNavigateSection('matching')}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="ms-ov-qa">
                                <OverviewAction
                                    label="Schedule Session"
                                    note="Book a 1:1 on an open mentor slot. Emails mentor and mentee."
                                    icon="bi-calendar-plus"
                                    onClick={() => setShowScheduleModal(true)}
                                />
                                <OverviewAction
                                    label="Log Interaction"
                                    note="Log a session or interaction for a Published pair."
                                    icon="bi-journal-plus"
                                    onClick={() => setShowLogModal(true)}
                                />
                                <OverviewAction
                                    label="Nudge Inactive Pairs"
                                    note="Visible only. Does not send."
                                    icon="bi-bell"
                                />
                                <OverviewAction
                                    label="Export Progress Report"
                                    note="Downloads pair progress (.xlsx). Not the Matching spreadsheet."
                                    icon="bi-file-earmark-spreadsheet"
                                    onClick={() => {
                                        void handleExportProgress();
                                    }}
                                />
                                <OverviewAction
                                    label="View Feedbacks"
                                    note="Available soon"
                                    icon="bi-chat-square-text"
                                    muted
                                />
                                <OverviewAction
                                    label="View Inactive Pairs"
                                    note="Opens Matching."
                                    icon="bi-exclamation-triangle"
                                    onClick={() => onNavigateSection('matching')}
                                />
                            </div>
                            {exportError ? <div className="text-danger small mt-2">{exportError}</div> : null}
                        </>
                    )}
                </div>
            </div>

            <div className="ms-ov-section">
                <div className="ms-ov-section-head">
                    <h4 className="ms-ov-section-title">Recent activity</h4>
                    <Link to={ACTIVITY_LOG_MENTORSHIP_HREF} className="ms-ov-section-link">
                        View all
                    </Link>
                </div>
                <div className="ms-ov-section-body">
                    {/* DEC-P7-008 honest placeholder: activity writers are not built yet, so the
                        feed stays empty. Internal ticket references removed from admin-facing copy. */}
                    <div className="ms-ov-muted-card mb-0">No activity yet.</div>
                </div>
            </div>
            <MentorshipLogInteractionModal
                show={showLogModal}
                cohortId={cohortAvailable ? cohortId : null}
                instituteId={instituteId}
                onClose={() => setShowLogModal(false)}
            />
            <MentorshipScheduleSessionModal
                show={showScheduleModal}
                cohortId={cohortAvailable ? cohortId : null}
                instituteId={instituteId}
                onClose={() => setShowScheduleModal(false)}
            />
        </div>
    );
};

const OverviewStatCard = ({
    title,
    value,
    note,
    loading = false,
    error = false,
    accent = 'muted',
    chip,
    chipTone = 'muted',
    sublabel,
    footer,
    variant = 'stat',
    lockState = null,
}: {
    title: string;
    value?: string | number | null;
    note?: string;
    loading?: boolean;
    error?: boolean;
    accent?: 'green' | 'blue' | 'muted' | 'amber';
    chip?: string;
    chipTone?: 'warn' | 'ok' | 'muted';
    sublabel?: string;
    footer?: JSX.Element | null;
    variant?: 'stat' | 'lock';
    lockState?: 'locked' | 'unlocked' | null;
}) => {
    const display = loading ? '...' : value == null || value === '' ? PLACEHOLDER : value;
    return (
        <div className="ms-ov-card">
            <div className="ms-ov-card-kicker">{title}</div>
            {variant === 'lock' ? (
                <div className={`ms-ov-lock-row${lockState === 'unlocked' ? ' is-open' : ''}`}>
                    <i className={`bi ${lockState === 'unlocked' ? 'bi-unlock' : 'bi-lock'}`} aria-hidden="true" />
                    <span>{display}</span>
                </div>
            ) : (
                <div className={`ms-ov-card-value is-${accent}`}>{display}</div>
            )}
            {sublabel ? <div className="ms-ov-card-sub">{sublabel}</div> : null}
            {chip ? <div><span className={`ms-ov-chip-mini is-${chipTone}`}>{chip}</span></div> : null}
            {note ? <div className={`ms-ov-card-note${error ? ' is-error' : ''}`}>{note}</div> : null}
            {footer}
        </div>
    );
};

const OverviewAction = ({
    label,
    note,
    onClick,
    icon,
    muted = false,
}: {
    label: string;
    note: string;
    onClick?: () => void;
    icon: string;
    muted?: boolean;
}) => (
    <button
        type="button"
        className={`ms-ov-qa-btn${muted ? ' ms-ov-muted-card' : ''}`}
        disabled={!onClick}
        title={note}
        onClick={onClick}>
        <span className="ms-ov-qa-icon">
            <i className={`bi ${icon}`} aria-hidden="true" />
        </span>
        <span className="ms-ov-qa-label">{label}</span>
        <span className="ms-ov-qa-note">{note}</span>
    </button>
);


export default MentorshipOverview;
